import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/auth';
import { useAuthRedirect } from './useAuthRedirect';
import { useCompanyId } from './useCompanyId';
import { isCompanyAdmin, isOperatorStaff } from '../utils/permission';
import { getCompany } from '../api/firebase/firestore/company/read';

export interface UseSecurityAddonPageAccessReturn {
  /** 認証 or 企業情報のロード中 */
  loading: boolean;
  /** 企業情報取得などで失敗した場合のエラーメッセージ */
  errorText: string;
  /** 対象企業でセキュリティアドオンが有効化されているか */
  hasSecurityAddon: boolean;
  /** 画面コンテンツを表示してよいか */
  canAccess: boolean;
  /** 代理モード時の対象企業 ID。自社ビューでは null になり得る */
  effectiveCompanyId: string | null;
  /** 読み取り対象として解決済みの企業 ID（自社 companyAdmin は自社 ID にフォールバック） */
  targetCompanyId: string | null;
}

/**
 * セキュリティ設定画面（設定 > セキュリティ）の共通アクセス制御フック（#2982-2985）。
 *
 * - 未ログイン → `/` に遷移
 * - companyAdmin / operatorStaff 以外 → `/home` に遷移（一般メンバーは閲覧不可）
 * - 対象企業の `has_security_addon` を取得して canAccess を判定
 *
 * API 機能（useApiPageAccess）と異なり利用開始日の概念は無く、フラグのみで判定する。
 */
export const useSecurityAddonPageAccess = (): UseSecurityAddonPageAccessReturn => {
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, isLoading: authLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();

  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [hasSecurityAddon, setHasSecurityAddon] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let active = true;
    const run = async (): Promise<void> => {
      if (!user) {
        return;
      }
      if (!isCompanyAdmin(user.role) && !isOperatorStaff(user.role)) {
        navigate('/home');
        return;
      }

      const targetCompanyId = effectiveCompanyId ?? user.company_id;
      if (!targetCompanyId) {
        setErrorText('企業情報が取得できませんでした。');
        setLoading(false);
        return;
      }

      try {
        const company = await getCompany(targetCompanyId);
        if (!active) return;
        if (!company) {
          setErrorText('企業情報が取得できませんでした。');
        } else {
          setHasSecurityAddon(company.has_security_addon === true);
        }
      } catch (e) {
        if (!active) return;
        setErrorText(e instanceof Error ? e.message : '企業情報の取得に失敗しました。');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [authLoading, user, navigate, effectiveCompanyId]);

  // companyAdmin / 代理モード / 運営スタッフのみアクセス可。
  const isCustomerView = isCompanyAdmin(user?.role) || isProxyMode;
  const canAccess = hasSecurityAddon && (isCustomerView || isOperatorStaff(user?.role));

  return {
    loading: loading || authLoading,
    errorText,
    hasSecurityAddon,
    canAccess,
    effectiveCompanyId,
    targetCompanyId: effectiveCompanyId ?? user?.company_id ?? null,
  };
};
